import React, { useEffect, useState } from "react";
import GenericTable from "./GenericTable";
import { Box, Typography, Button, Avatar } from "@mui/material";
import { useTranslation } from "react-i18next";
import { Edit, Delete } from "@mui/icons-material";
import CreateProjectPhaseDialog, { CreateProjectCategoryDialog } from "./dialogs/CreateProjectPhaseDialog";
import CreateProjectTaskDialog from "./dialogs/CreateProjectTaskDialog";
import { fetchProjectById, updateProjectTask } from "../api/projects";
import { generateColorFromId } from "../utils/utilFunctions";
import { useAppContext } from "../context/AppContext";

const ProjectDetails = ({ selectedProject, projectUpdated }) => {
    const { t } = useTranslation();

    const { employeesAppContext, user } = useAppContext();
    const [phaseDialogOpen, setPhaseDialogOpen] = useState(false);
    const [categoryDialogOpen, setCategoryDialogOpen] = useState(false);
    const [taskDialogOpen, setTaskDialogOpen] = useState(false);
    const [selectedPhase, setSelectedPhase] = useState(null);
    const [selectedCategory, setSelectedCategory] = useState(null);
    const [selectedTask, setSelectedTask] = useState(null);
    const [project, setProject] = useState(selectedProject);

    useEffect(() => {
        if (selectedProject) {
            setProject(selectedProject);
        }
    }, [selectedProject]);

    const columnsPhases = [
        { field: "title", label: t("common.title"), type: "title" },
        { field: "description", label: t("common.description") },
        { field: "deadline", label: t("common.deadline"), type: "date" },
        { field: "total_time_logged", type: "hours", label: t("fields.total_time_logged") },
    ];

    const columnsCategories = [
        { field: "title", label: t("common.title"), type: "title" },
        { field: "description", label: t("common.description") },
        { field: "deadline", label: t("common.deadline"), type: "date" },
        { field: "total_time_logged", type: "hours", label: t("fields.total_time_logged") },
    ];

    const columnsTasks = [
        { field: "title", label: t("common.title"), type: "title" },
        { field: "description", label: t("common.description") },
        { field: "project_phase_title", label: t("common.phase") },
        { field: "project_category_title", label: t("common.category") },
        { field: "estimated_hours", label: t("templates.estimated_hours") },
        { field: "deadline", label: t("common.deadline"), type: "date" },
        { field: "total_time_logged", type: "hours", label: t("fields.total_time_logged") },
        {
            field: "status",
            label: t("projects.status"),
            editable: true,
            type: "status",
            options: [
                { label: t("task_statuses.to_do"), value: "to_do" },
                { label: t("task_statuses.in_progress"), value: "in_progress" },
                { label: t("task_statuses.blocked"), value: "blocked" },
                { label: t("task_statuses.needs_review"), value: "needs_review" },
                { label: t("task_statuses.completed"), value: "completed" },
                { label: t("task_statuses.cancelled"), value: "cancelled" }
            ]
        },
    ];

    const handleCreatePhase = () => {
        setSelectedPhase(null);
        setPhaseDialogOpen(true);
    };

    const handleCreateCategory = () => {
        setSelectedCategory(null);
        setCategoryDialogOpen(true);
    };

    const handleCreateTask = () => {
        setSelectedTask(null);
        setTaskDialogOpen(true);
    };

    const handleEditPhase = (phase) => {
        setSelectedPhase(phase);
        setPhaseDialogOpen(true);
    };

    const handleEditCategory = (category) => {
        setSelectedCategory(category);
        setCategoryDialogOpen(true);
    };

    const handleEditTask = (task) => {
        setSelectedTask(task);
        setTaskDialogOpen(true);
    };

    const updateTask = async (row, field, oldValue, newValue) => {
        try {
            const updatedProject = await updateProjectTask(row.id, { ...row, deadline: row?.deadline?.split("T")[0] ?? null, status: newValue });
            const data = await fetchProjectById(project.id);
            console.log('data: ', data);
            setProject(data);
        } catch (err) {
            console.log('err: ', err);
        }
    };

    const handleDeletePhase = (id) => {
        console.log("Deleting phase:", id);
        // Add logic to delete the phase
    };

    const handleDeleteCategory = (id) => {
        console.log("Deleting category:", id);
        // Add logic to delete the category
    };

    const handleDeleteTask = (id) => {
        console.log("Deleting task:", id);
        // Add logic to delete the task
    };

    return (
        <Box sx={{ flexDirection: 'column', display: 'flex' }}>
            {/* Project Overview */}
            <Typography variant="h4" gutterBottom>
                {project.title} - {t("common.details")}
            </Typography>

            {/* Wrap in Box to ensure it takes up full width and behaves responsively */}
            <Box sx={{ width: '100%', display: 'flex', flexWrap: 'wrap', gap: 3 }}>
                {/* Phases Table */}
                <Box sx={{ flex: 1, minWidth: '400px' }}>
                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                        <Typography variant="h6">{t("templates.project_phases")}</Typography>
                        <Button
                            onClick={handleCreatePhase}
                            variant="contained"
                            sx={{
                                bgcolor: "#444",
                                color: "#fff",
                                fontWeight: 500,
                                borderRadius: 2,
                                textTransform: "none",
                                "&:hover": { bgcolor: "#333" },
                            }}
                        >
                            {t("templates.create_category")}
                        </Button>
                    </Box>
                    <GenericTable
                        columns={columnsPhases}
                        data={project.project_phases}
                        actions={[
                            { icon: <Edit />, onClick: handleEditPhase },
                            { icon: <Delete />, onClick: handleDeletePhase }
                        ]}
                    />
                </Box>

                {/* Categories Table */}
                <Box sx={{ flex: 1, minWidth: '400px' }}>
                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                        <Typography variant="h6">{t("templates.project_categories")}</Typography>
                        <Button
                            onClick={handleCreateCategory}
                            variant="contained"
                            sx={{
                                bgcolor: "#444",
                                color: "#fff",
                                fontWeight: 500,
                                borderRadius: 2,
                                textTransform: "none",
                                "&:hover": { bgcolor: "#333" },
                            }}
                        >
                            {t("templates.create_phase")}
                        </Button>
                    </Box>
                    <GenericTable
                        columns={columnsCategories}
                        data={project.project_categories}
                        actions={[
                            { icon: <Edit />, onClick: handleEditCategory },
                            { icon: <Delete />, onClick: handleDeleteCategory }
                        ]}
                    />
                </Box>
            </Box>

            {/* Tasks Table */}
            <Box sx={{ marginTop: 3 }}>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                    <Typography variant="h6">{t("templates.tasks")}</Typography>
                    <Button
                        onClick={handleCreateTask}
                        variant="contained"
                        sx={{
                            bgcolor: "#444",
                            color: "#fff",
                            fontWeight: 500,
                            borderRadius: 2,
                            textTransform: "none",
                            "&:hover": { bgcolor: "#333" },
                        }}
                    >
                        {t("templates.create_task")}
                    </Button>
                </Box>
                <GenericTable
                    columns={columnsTasks}
                    data={project.tasks}
                    onUpdate={updateTask}
                    actions={[
                        { icon: <Edit />, onClick: handleEditTask },
                        { icon: <Delete />, onClick: handleDeleteTask }
                    ]}
                    renderExpandedRow={(task) => {
                        console.log('task: ', task);
                        return <Box sx={{ px: 2, py: 1, backgroundColor: '#f9f9f9', color: 'black' }}>
                            {task.time_logs && task.time_logs.length > 0 ? (
                                <Box>
                                    <Typography variant="subtitle2" gutterBottom>
                                        {t("common.log_time")}:
                                    </Typography>
                                    {task.time_logs.map((log) => {
                                        const employee = employeesAppContext.find(emp => emp.id === log.user_id); // Find the employee by user_id
                                        const canDelete =
                                            user &&
                                            (['owner', 'admin', 'manager'].includes(user.role) || user.id === log.user_id);

                                        return (
                                            <Box key={log.id} sx={{ mb: 1, pl: 1, display: 'flex', alignItems: 'center' }}>

                                                <Typography variant="body2">
                                                    •
                                                </Typography>
                                                {employee && (
                                                    <Avatar
                                                        sx={{
                                                            width: 30,
                                                            height: 30,
                                                            marginLeft: '8px',
                                                            marginRight: '8px',
                                                            color: 'black',
                                                            cursor: 'pointer',
                                                            backgroundColor: generateColorFromId(`${employee.id}${employee.id}${employee.id}`),
                                                        }}
                                                    >
                                                        {employee.name[0]} {/* Display first letter of employee's name */}
                                                    </Avatar>
                                                )}
                                                <Typography variant="body2">
                                                    {new Date(log.logged_date).toLocaleDateString()} —
                                                    {Math.floor(log.logged_minutes / 60)}h {log.logged_minutes % 60}m
                                                    ({log.logged_minutes} min)
                                                </Typography>
                                            </Box>
                                        );
                                    })}
                                    <Typography sx={{ fontWeight: 'bold', mt: 1 }}>
                                        Total: {Math.floor(task.total_time_logged / 60)}h {task.total_time_logged % 60}m ({task.total_time_logged} min)
                                    </Typography>

                                </Box>
                            ) : (
                                <Typography>{t('common.no_time_logged')}</Typography>
                            )}
                        </Box>
                    }}
                />
            </Box>

            {/* Dialogs */}
            <CreateProjectPhaseDialog
                open={phaseDialogOpen}
                onClose={() => setPhaseDialogOpen(false)}
                projectId={project.id}
                phase={selectedPhase}
                onSuccess={() => projectUpdated(project.id)}
            />
            <CreateProjectCategoryDialog
                open={categoryDialogOpen}
                onClose={() => setCategoryDialogOpen(false)}
                projectId={project.id}
                category={selectedCategory}
                onSuccess={() => projectUpdated(project.id)}
            />
            <CreateProjectTaskDialog
                open={taskDialogOpen}
                onClose={() => setTaskDialogOpen(false)}
                projectId={project.id}
                task={selectedTask}
                onSuccess={() => projectUpdated(project.id)}
                categories={project?.project_categories}
                phases={project?.project_phases}
            />
        </Box>
    );
};

export default ProjectDetails;
